import { useEffect, useState, useMemo } from 'react'
import { Layout, Menu, Drawer, Button } from 'antd'
import { MenuOutlined, CloseOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Icon, { QuestionCircleOutlined } from '@ant-design/icons'
import { ReactComponent as PatientIconFilled } from '../../assets/Icons_ Patient Fill.svg'
import { ReactComponent as AlertIconIconFilled } from '../../assets/Icons_Alerts Fill.svg'
import { ReactComponent as DashboardIconFilled } from '../../assets/Icons_Dashboard  fill.svg'
import { ReactComponent as DoctorIconFilled } from '../../assets/Icons_Doctor fill.svg'
import { ReactComponent as NurseIconIconFilled } from '../../assets/Icons_Nurse Fill.svg'
import {
  ALERTS,
  APP_PATH,
  DASHBOARD,
  DOCTORS,
  HELP,
  NURSES,
  PATIENTS,
} from '../../constants/pathConstant'
import CyBrandLogo from '../CyBrandLogo/CyBrandLogo'

const { Sider } = Layout

const StyledSider = styled(Sider)`
  min-height: 100vh;
  transition: width 0.3s ease;

  & .ant-menu {
    overflow: hidden;
  }

  & .ant-menu-item {
    margin-bottom: 25px;
    border-radius: 4px;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
  }

  /* When the sidebar is expanded (desktop view) */
  &:not(.ant-layout-sider-collapsed) {
    & .ant-menu-item {
      justify-content: flex-start; /* Align text to left */
    }

    & .ant-menu-title-content {
      text-align: left; /* Ensure text starts from left */
      margin-left: 15px; /* Adds spacing between icon & text */
    }
  }

  /* When the sidebar is collapsed */
  &.ant-layout-sider-collapsed {
    width: 80px !important; /* Fix overflow issue */
    min-width: 80px !important;
    max-width: 80px !important;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    
    & .ant-menu {
      width: 90%;
    }

    & .ant-menu-item {
      display: flex;
      align-items: center;
      justify-content: center;      
      padding: 0;      
      margin-bottom: 25px;
      width: 100%;
      
      & .ant-menu-title-content {
        display: none; /* Hide text when collapsed */
      }
    }
  } 
`;

/* Brand Logo Styling */
const BrandLogoContainer = styled.div<{ collapsed: boolean; isMobile: boolean; drawerVisible: boolean }>`
  padding: 20px;
  width: 100%;
  display: flex;
  justify-content: center;  
  align-items: center;
  transition: all 0.3s ease;

  ${({ isMobile, drawerVisible }) =>
    isMobile && drawerVisible
      ? `
    padding: 30px; /* Increase padding for larger appearance */
  `
      : ''}
`;


/* Extra Spacing Between Icon & Text */
const MenuItemText = styled.span`
  margin-left: 0px; /* Adds spacing */
  transition: margin-left 0.3s ease; /* Smooth transition */
  padding: 10px 20px;
  font-size: medium;
`;


const MobileMenuButton = styled(Button)`
  display: none;
  position: absolute;
  top: 15px;
  left: 15px;
  z-index: 0;

  @media (max-width: 1200px) {
    display: block;
  }
`

const DrawerCloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px; /* Move close icon to the right */
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px;
  z-index: 1001; /* Ensure it's always on top */

  &:hover {
    opacity: 0.8;
  }
`;
function CySider({ currentPath }: { currentPath: string }) {
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(window.innerWidth <= 1200)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200)
  const [drawerVisible, setDrawerVisible] = useState(false)



  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1200;
      setIsMobile(mobile);
      setCollapsed(mobile);

    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = useMemo(() => [
    {
      key: DASHBOARD,
      icon: <Icon component={DashboardIconFilled} style={{ fontSize: '150%' }} />,
      label: <MenuItemText>Dashboard</MenuItemText>,
    },
    {
      key: PATIENTS,
      icon: <Icon component={PatientIconFilled} style={{ fontSize: '150%' }} />,
      label: <MenuItemText>Patients</MenuItemText>,
    },
    {
      key: DOCTORS,
      icon: <Icon component={DoctorIconFilled} style={{ fontSize: '150%' }} />,
      label: <MenuItemText>Doctors</MenuItemText>,
    },
    {
      key: NURSES,
      icon: <Icon component={NurseIconIconFilled} style={{ fontSize: '150%' }} />,
      label: <MenuItemText>Nurses</MenuItemText>,
    },
    {
      key: ALERTS,
      icon: <Icon component={AlertIconIconFilled} style={{ fontSize: '150%' }} />,
      label: <MenuItemText>Alerts</MenuItemText>,
    },
    {
      key: HELP,
      icon: <QuestionCircleOutlined style={{ fontSize: '180%' }} />,
      label: <MenuItemText>Help</MenuItemText>,
    },
  ], [])



  const sidebarContent = (
    <>
      {/* Brand Logo - Always Large When Expanded */}
      <BrandLogoContainer collapsed={collapsed} isMobile={isMobile} drawerVisible={drawerVisible}>
        <CyBrandLogo size={isMobile && drawerVisible ? 'large' : collapsed ? 'medium' : 'large'} />
      </BrandLogoContainer>



      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[currentPath]}
        onClick={({ key }) => {
          if (key === HELP) {
            const helpUrl = APP_PATH === '/' ? '/user_guide.html' : `/${APP_PATH}/user_guide.html`
            window.open(helpUrl, '_blank')
            if (isMobile) setDrawerVisible(false)
          } else {
            navigate(key)
            if (isMobile) {
              setDrawerVisible(false)
            }
          }
        }}
        items={menuItems}
      />
    </>
  );


  return (
    <>
      {/* Mobile menu button */}
      {isMobile && (
        <MobileMenuButton
          type="primary"
          icon={<MenuOutlined />}
          onClick={() => setDrawerVisible(true)}
        />
      )}

      {/* Sidebar (Desktop Mode) */}
      {!isMobile ? (
        <StyledSider
          theme="dark"
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          style={{ textAlign: 'center' }}
        >
          {sidebarContent}
        </StyledSider>
      ) : (
        /* Drawer (Mobile Mode) */
        <Drawer
          placement="left"
          closable={false}
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          width={'90%'}
          height={'100vh'}
          zIndex={0}
          style={{
            background: '#001529',
            height: '100%',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            position: 'relative'
          }}
        >
          <DrawerCloseButton onClick={() => setDrawerVisible(false)}>
            <CloseOutlined style={{ color: 'white', fontSize: '22px' }} />
          </DrawerCloseButton>
          {sidebarContent}
        </Drawer>
      )}
    </>
  )
}

export default CySider
